<template lang="pug">
.response-rate-accordion-accordion
  b-card-header.p-2(header-tag="header" role="tab" header-class="accordion-toggle" v-b-toggle="'accordion-response-rate'")
    span.toggle-text 回答率
  b-collapse(:id="'accordion-response-rate'" :accordion="'accordion-response-rate'" role="tabpanel")
    b-card-body.p-2(v-if="isBusy")
      AccordionLoading(:isBusy="isBusy")
    b-card-body(v-else)
      // rate
      .mb-0
        span.key-name 回答率
      #response-rate.mb-3
        .mb-0
          span {{ _responseRateOfAll }}
      // response rate for each other user group
      template(v-for="(item, index) in responseRateDetailItems")
        .mb-0
          span.key-name 回答率（{{ item.groupTypeName }}・{{ item.groupName }}）
        div(:id="'response-rate' + index" :class="getClass(index)")
          .mb-0
            span {{ createResponseCount(item) }}
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";
import { MobileSendSafetyConfirmDetailToOtherUserState } from "@/store/MobileSendSafetyConfirmDetailToOtherUser";

import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import OtherUserGroupSendMessageReadMembersService, { OtherUserGroupSendMessageReadMemberModel as ReadMemberModel } from "@/commons/services/OtherUserGroupSendMessageReadMembers";

import AccordionLoading from "@/mobile/basics/AccordionLoading.vue";

/**
 * ### 安否確認送信(施設関係者)の回答率
 */
@Component({
  components: {
    AccordionLoading
  }
})
export default class ResponseRateAccordion extends Vue {
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;
  /** 送信メッセージにひもづくメンバーサービス */
  @Inject("OtherUserGroupSendMessageReadMembersService") otherUserGroupSendMessageReadMembersService!: OtherUserGroupSendMessageReadMembersService;

  /** ### 安否確認詳細画面で必要な情報 */
  @Getter("mobileSendSafetyConfirmDetailToOtherUserState") state!: MobileSendSafetyConfirmDetailToOtherUserState;

  /** 安否確認 施設関係者メッセージソートキー */
  otherUserMessagesSortKey!: string[];
  /** ローディングの表示可否 */
  isBusy: boolean = false;
  /** 表示対象家族 */
  otherUsers: Array<ReadMemberModel> = [];
  /** 施設関係者グループ名MAP */
  otherUserGroupTypeMap: Map<number, string> = new Map();

  /** 全体の回答者数 */
  allRresponseCount: number = 0;
  /** 全体の未回答者数 */
  allNonResponseCount: number = 0;
  /** 全体の回答率 */
  responseRateOfAll: string = "";
  /** 施設関係差yグループ毎の回答情報 */
  responseRateDetailItems: Array<ResponseRateAccordionItem> = [];

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.otherUserMessagesSortKey = this.state.otherUserMessagesSortKey as string[];
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - 画面表示用に返信未読数をカウントする
   * - 未読・既読の家族を表示するコンポーネント用の情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypeMap: Map<number, string> = this.createOtherUserGroupTypesMap(otherUserGroupTypes);
    this.otherUserGroupTypeMap = otherUserGroupTypeMap;

    if (this.otherUserMessagesSortKey != undefined) {
      let otherUsers: Array<ReadMemberModel> = [];

      for (const sortKey of this.otherUserMessagesSortKey) {
        const splitedSortKey: string[] = sortKey.split("_");
        const datetime: number = Number(splitedSortKey[2]);
        const readOtherUsers: Array<ReadMemberModel> = await this.getSendMessageReadOtherUsers(datetime);
        otherUsers = otherUsers.concat(readOtherUsers);
      }
      this.otherUsers = otherUsers;

      await this.setResponseRate(otherUsers, otherUserGroupTypeMap);
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);
    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypesMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();
    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }
    return map;
  }

  /**
   * ### 施設関係者の回答率を格納する
   */
  public async setResponseRate(otherUsers: Array<ReadMemberModel>, otherUserGroupTypeMap: Map<number, string>): Promise<void> {
    let allRresponseCount: number = 0;
    let allNonResponseCount: number = 0;
    let groupMembersToIdMap: Map<number, Array<ReadMemberModel>> = new Map<number, Array<ReadMemberModel>>();

    for (const otherUser of otherUsers) {
      if (otherUser.readDatetime != undefined || otherUser.readDatetime != null) {
        allRresponseCount++;
      } else {
        allNonResponseCount++;
      }

      // 施設関係者グループIDをkey、メンバー配列をvalueにしたMAPに格納する
      const groupTypeName: string | undefined = otherUserGroupTypeMap.get(otherUser.groupTypeId);
      otherUser.groupTypeName = groupTypeName != undefined ? groupTypeName : "";
      let groupMembers: Array<ReadMemberModel> | undefined = groupMembersToIdMap.get(otherUser.groupId);
      if (groupMembers != undefined) {
        groupMembers.push(otherUser);
        groupMembersToIdMap.set(otherUser.groupId, groupMembers);
      } else {
        groupMembersToIdMap.set(otherUser.groupId, [otherUser]);
      }
    }

    this.allRresponseCount = allRresponseCount;
    this.allNonResponseCount = allNonResponseCount;
    const percentage: number = ((allRresponseCount / (allRresponseCount + allNonResponseCount)) * 10000) / 100;
    this.responseRateOfAll = percentage.toFixed(2);

    let items: Array<ResponseRateAccordionItem> = [];
    groupMembersToIdMap.forEach((members: Array<ReadMemberModel>, id: number) => {
      let responseCount: number = 0;
      let nonResponseCount: number = 0;

      for (const member of members) {
        if (member.readDatetime != undefined || member.readDatetime != null) {
          responseCount++;
        } else {
          nonResponseCount++;
        }
      }

      const percentage: number = ((responseCount / members.length) * 10000) / 100;
      const responseRate: string = percentage.toFixed(2);

      items.push({
        id,
        groupTypeName: members[0].groupTypeName,
        groupName: members[0].groupName,
        targetCount: members.length,
        responseCount,
        nonResponseCount,
        responseRate
      });
    });
    this.responseRateDetailItems = items;

    return;
  }

  /**
   * ### メッセージ送信したメッセージにひもづく施設関係者を取得する
   */
  public async getSendMessageReadOtherUsers(datetime: number): Promise<Array<ReadMemberModel>> {
    const otherUsers: Array<ReadMemberModel> = await this.otherUserGroupSendMessageReadMembersService.get(userStore, datetime);

    return otherUsers;
  }

  public createResponseCount(item: ResponseRateAccordionItem): string {
    return `${item.responseCount}／${item.targetCount}人（${item.responseRate}％）`;
  }

  public getClass(index: number): string {
    if (index + 1 != this.responseRateDetailItems.length) {
      return "mb-3";
    } else {
      return "";
    }
  }

  get _responseRateOfAll(): string {
    if (this.responseRateOfAll == "") {
      return "";
    }
    return `${this.allRresponseCount}／${this.allRresponseCount + this.allNonResponseCount}人（${this.responseRateOfAll}％）`;
  }
}

export interface ResponseRateAccordionItem {
  /** グループID */
  id: number;
  /** 種類 */
  groupTypeName: string;
  /** グループ名 */
  groupName: string;
  /** 宛先人数 */
  targetCount: number;
  /** 回答者数 */
  responseCount: number;
  /** 未回答者数 */
  nonResponseCount: number;
  /** 回答率 */
  responseRate: string;
}
</script>
